import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import './i18n'; // Initialize i18n
import Navbar from './components/Navbar';
import FeedbackButton from './components/FeedbackButton';
import { ToastNotificationContainer } from './components/ToastNotification';
import PrivateRoute from './components/PrivateRoute';
import Landing from './pages/Landing';
import LoginNew from './pages/LoginNew';
import SignupNew from './pages/SignupNew';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmationPending from './pages/ConfirmationPending';
import ConfirmEmail from './pages/ConfirmEmail';
import Dashboard from './pages/Dashboard';
import Catalog from './pages/Catalog';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import DocumentDetailNew from './pages/DocumentDetailNew';
import AnonymousAnalysis from './pages/AnonymousAnalysis';
import TermosDeUso from './pages/TermosDeUso';
import PoliticaDePrivacidade from './pages/PoliticaDePrivacidade';
import PoliticaDeCookies from './pages/PoliticaDeCookies';
import AvisoLegal from './pages/AvisoLegal';
import NotFound from './pages/NotFound';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import './AppNew.css';
import trackingAPI, { getBrowserGeo, getOrCreateTrackingSessionId } from './services/tracking';
import { campaignAPI } from './services/api';

// Remove language prefix from path for matching
const removeLanguagePrefix = (pathname: string): string => {
  if (pathname.startsWith('/en/')) return pathname.substring(3);
  if (pathname === '/en') return '/';
  return pathname;
};

// Check if current path is English
const isEnglishPath = (pathname: string): boolean => {
  return pathname.startsWith('/en/') || pathname === '/en';
};

// Wrapper to conditionally show Navbar
const getTitleForPath = (pathname: string, lang: string) => {
  const basePath = removeLanguagePrefix(pathname);
  const isEn = lang === 'en';

  if (basePath === '/' || basePath === '') return 'ContratoSeguro';
  if (basePath === '/login') return isEn ? 'Sign In | ContratoSeguro' : 'Entrar | ContratoSeguro';
  if (basePath === '/signup') return isEn ? 'Sign Up | ContratoSeguro' : 'Criar Conta | ContratoSeguro';
  if (basePath === '/recuperar-senha') return isEn ? 'Recover Password | ContratoSeguro' : 'Recuperar Senha | ContratoSeguro';
  if (basePath === '/reset-password') return isEn ? 'Reset Password | ContratoSeguro' : 'Redefinir Senha | ContratoSeguro';
  if (basePath === '/confirmation-pending') return isEn ? 'Confirm Email | ContratoSeguro' : 'Confirme seu E-mail | ContratoSeguro';
  if (basePath === '/confirm-email') return isEn ? 'Email Confirmation | ContratoSeguro' : 'Confirmação de E-mail | ContratoSeguro';
  if (basePath === '/upload' || basePath === '/dashboard') return isEn ? 'Upload | ContratoSeguro' : 'Envio | ContratoSeguro';
  if (basePath === '/catalog') return isEn ? 'My Documents | ContratoSeguro' : 'Meus Documentos | ContratoSeguro';
  if (basePath === '/profile') return isEn ? 'Profile | ContratoSeguro' : 'Perfil | ContratoSeguro';
  if (basePath === '/notifications') return isEn ? 'Notifications | ContratoSeguro' : 'Notificações | ContratoSeguro';
  if (basePath === '/termos-de-uso') return isEn ? 'Terms of Use | ContratoSeguro' : 'Termos de Uso | ContratoSeguro';
  if (basePath === '/politica-de-privacidade') return isEn ? 'Privacy Policy | ContratoSeguro' : 'Política de Privacidade | ContratoSeguro';
  if (basePath === '/politica-de-cookies') return isEn ? 'Cookies Policy | ContratoSeguro' : 'Política de Cookies | ContratoSeguro';
  if (basePath === '/aviso-legal') return isEn ? 'Legal Notice | ContratoSeguro' : 'Aviso Legal | ContratoSeguro';
  if (basePath === '/admin') return 'Admin | ContratoSeguro';
  if (basePath === '/admin/login') return 'Admin Login | ContratoSeguro';
  if (basePath.startsWith('/documents/')) return isEn ? 'Document | ContratoSeguro' : 'Documento | ContratoSeguro';
  if (basePath.startsWith('/preview/')) return isEn ? 'Preview | ContratoSeguro' : 'Prévia | ContratoSeguro';
  return 'ContratoSeguro';
};

const getPageIdentifier = (pathname: string) => {
  const basePath = removeLanguagePrefix(pathname);
  if (basePath === '/' || basePath === '') return 'landing';
  if (basePath === '/login') return 'login';
  if (basePath === '/signup') return 'signup';
  if (basePath === '/upload' || basePath === '/dashboard') return 'upload';
  if (basePath === '/catalog') return 'catalog';
  if (basePath === '/profile') return 'profile';
  if (basePath === '/notifications') return 'notifications';
  if (basePath.startsWith('/documents/')) return 'document_detail';
  if (basePath.startsWith('/preview/')) return 'anonymous_preview';
  if (basePath.startsWith('/admin')) return 'admin';
  return basePath.replace(/^\//, '') || 'unknown';
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lastTrackedPath = React.useRef<string>('');

  // Sync language with URL path
  useEffect(() => {
    const pathLang = isEnglishPath(location.pathname) ? 'en' : 'pt';
    const basePath = removeLanguagePrefix(location.pathname);

    // Skip language-based redirects for admin routes (admin auth is separate from app users)
    if (basePath.startsWith('/admin')) {
      return;
    }

    // If the user has a preferred language, align the URL to it to avoid ping-ponging
    if (user?.language && pathLang !== user.language) {
      const targetPath = user.language === 'en' ? `/en${basePath}` : basePath;
      if (targetPath !== location.pathname) {
        navigate(`${targetPath}${location.search}${location.hash}`, { replace: true });
      }
      if (i18n.language !== user.language) {
        i18n.changeLanguage(user.language);
      }
      return;
    }

    if (i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [location.pathname, location.search, location.hash, user?.language, i18n, navigate]);

  // Sync language with user preference on login
  useEffect(() => {
    if (user?.language && i18n.language !== user.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.language, i18n]);

  React.useEffect(() => {
    document.title = getTitleForPath(location.pathname, i18n.language);
  }, [location.pathname, i18n.language]);

  React.useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      return;
    }

    const pathKey = `${location.pathname}${location.search}`;
    if (pathKey === lastTrackedPath.current) {
      return;
    }
    lastTrackedPath.current = pathKey;

    const searchParams = new URLSearchParams(location.search);
    const utmSource = searchParams.get('utm_source') || searchParams.get('source');
    const persistedSource = localStorage.getItem('traffic_source');
    const campaignSource = utmSource || persistedSource;
    if (utmSource) {
      localStorage.setItem('traffic_source', utmSource);
      localStorage.setItem('campaign_source', utmSource);
      localStorage.setItem('campaign_first_seen_at', new Date().toISOString());
      localStorage.setItem('campaign_landing_page', window.location.pathname + window.location.search);
    }

    const source = campaignSource || 'direct';
    const referrer = document.referrer || null;
    const sessionId = getOrCreateTrackingSessionId();
    const page = getPageIdentifier(location.pathname);

    (async () => {
      let geo;
      try {
        geo = await getBrowserGeo();
      } catch {
        geo = undefined;
      }

      trackingAPI.trackPageVisit({
        page,
        source,
        referrer,
        session_id: sessionId,
        user_agent: navigator.userAgent,
        geo,
      }).catch((err) => {
        console.debug('Failed to track page visit', err);
      });

      if (source && source !== 'direct') {
        campaignAPI.trackVisit({
          source,
          landing_page: localStorage.getItem('campaign_landing_page') || window.location.pathname,
          session_id: sessionId,
        }).catch((err) => {
          console.debug('Failed to track campaign visit', err);
        });
      }
    })();
  }, [location.pathname, location.search]);

  const basePath = removeLanguagePrefix(location.pathname);
  const hideNavbar = basePath === '/' || basePath === '' || basePath.startsWith('/admin');
  const showFeedback = !basePath.startsWith('/admin');

  return (
    <div className="app">
      {user && !hideNavbar && <Navbar />}
      {children}
      {showFeedback && <FeedbackButton />}
      <ToastNotificationContainer />
    </div>
  );
};

function App() {
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <AppLayout>
              <Routes>
              {/* Portuguese routes (default) */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<LoginNew />} />
              <Route path="/signup" element={<SignupNew />} />
              <Route path="/recuperar-senha" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirmation-pending" element={<ConfirmationPending />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/preview/:sessionId" element={<AnonymousAnalysis />} />
              <Route path="/termos-de-uso" element={<TermosDeUso />} />
              <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
              <Route path="/politica-de-cookies" element={<PoliticaDeCookies />} />
              <Route path="/aviso-legal" element={<AvisoLegal />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/upload" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/dashboard" element={<Navigate to="/upload" replace />} />
              <Route path="/catalog" element={<PrivateRoute><Catalog /></PrivateRoute>} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/documents/:id" element={<PrivateRoute><DocumentDetailNew /></PrivateRoute>} />

              {/* English routes (/en/*) */}
              <Route path="/en" element={<Landing />} />
              <Route path="/en/login" element={<LoginNew />} />
              <Route path="/en/signup" element={<SignupNew />} />
              <Route path="/en/recuperar-senha" element={<ForgotPassword />} />
              <Route path="/en/reset-password" element={<ResetPassword />} />
              <Route path="/en/confirmation-pending" element={<ConfirmationPending />} />
              <Route path="/en/confirm-email" element={<ConfirmEmail />} />
              <Route path="/en/preview/:sessionId" element={<AnonymousAnalysis />} />
              <Route path="/en/termos-de-uso" element={<TermosDeUso />} />
              <Route path="/en/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
              <Route path="/en/politica-de-cookies" element={<PoliticaDeCookies />} />
              <Route path="/en/aviso-legal" element={<AvisoLegal />} />
              <Route path="/en/upload" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/en/dashboard" element={<Navigate to="/en/upload" replace />} />
              <Route path="/en/catalog" element={<PrivateRoute><Catalog /></PrivateRoute>} />
              <Route path="/en/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/en/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
              <Route path="/en/documents/:id" element={<PrivateRoute><DocumentDetailNew /></PrivateRoute>} />

              <Route path="*" element={<NotFound />} />
              </Routes>
            </AppLayout>
          </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
